import React, { useState } from 'react';
import { api } from '../api.js';
import { Card, ErrorBanner, Loading, Modal, useAsync, useSession, useToast } from '../ui.jsx';
import { centsToInput, dateLabel, money, monthLabel, nameOf, parseAmountToCents, unitName } from '../lib/format.js';
import { Link } from '../main.jsx';

const ORIGIN_LABELS = {
  adjustment: '調整應付款',
  overpayment: '確認超額付款',
  cancellation: '取消付款項目',
  historical: '歷史補錄',
};

/**
 * 結餘 stay pending until an administrator records them as returned or transferred
 * to another 付款項目; the system never offsets them automatically.
 */
export default function BalancesPage() {
  const session = useSession();
  const notify = useToast();
  const balances = useAsync(() => api.balances(), []);
  const [dialog, setDialog] = useState(null);
  const [error, setError] = useState(null);

  if (balances.loading) return <Loading />;
  if (balances.error) return <ErrorBanner error={balances.error} />;

  const act = async (action, message) => {
    setError(null);
    try {
      await action();
      notify(message);
      setDialog(null);
      balances.reload();
    } catch (failure) {
      setError(failure);
    }
  };

  const pending = balances.data.balances.filter((balance) => balance.pending_cents > 0);
  const resolved = balances.data.balances.filter((balance) => balance.pending_cents === 0);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <div className="eyebrow">帳簿控制台</div>
          <h1>待處理結餘</h1>
          <p className="small muted">結餘必須明確記錄為已退回或已轉用；系統不會因成員另有應付款而自動抵銷。</p>
        </div>
        <div className="metric"><span>待處理合計</span><strong data-testid="balance-pending-total">{money(balances.data.pending_total_cents)}</strong></div>
      </div>

      <ErrorBanner error={error} />

      <Card title="待處理" testId="pending-balances">
        {pending.length === 0 ? <p className="small muted" data-testid="no-pending-balances">目前沒有待處理結餘。</p> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>付款項目</th>
                  <th>成員</th>
                  <th>來源</th>
                  <th className="num">結餘</th>
                  <th className="num">待處理</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pending.map((balance) => (
                  <tr key={balance.id} data-testid={`balance-${balance.id}`}>
                    <td>
                      <Link to={`/items/${balance.item_id}`}>{balance.item_title}</Link>
                      <div className="small muted">
                        {unitName(session.all_units, balance.unit_id)} · {monthLabel(balance.accounting_month)}
                        {balance.item_status === 'cancelled' ? ' · 已取消' : ''}
                      </div>
                    </td>
                    <td>{nameOf(session.members, balance.member_id)}</td>
                    <td>{ORIGIN_LABELS[balance.origin] || balance.origin}</td>
                    <td className="num">{money(balance.amount_cents)}</td>
                    <td className="num">{money(balance.pending_cents)}</td>
                    <td>
                      <div className="row">
                        <button type="button" className="small" onClick={() => setDialog({ kind: 'return', balance })} data-testid={`balance-return-${balance.id}`}>記錄已退回</button>
                        <button type="button" className="small" onClick={() => setDialog({ kind: 'transfer', balance })} data-testid={`balance-transfer-${balance.id}`}>轉用至其他項目</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="已完成處理" testId="resolved-balances">
        {resolved.length === 0 ? <p className="small muted">尚未有已完成處理的結餘。</p> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>付款項目</th><th>成員</th><th className="num">金額</th><th>處理方式</th></tr>
              </thead>
              <tbody>
                {resolved.map((balance) => (
                  <tr key={balance.id} data-testid={`resolved-balance-${balance.id}`}>
                    <td><Link to={`/items/${balance.item_id}`}>{balance.item_title}</Link></td>
                    <td>{nameOf(session.members, balance.member_id)}</td>
                    <td className="num">{money(balance.amount_cents)}</td>
                    <td className="small">
                      {balance.dispositions.map((disposition) => (
                        <div key={disposition.id}>
                          {disposition.kind === 'returned' ? '已退回' : '已轉用'} {money(disposition.amount_cents)} · {dateLabel(disposition.handled_on)}
                          {disposition.target_item_id ? <> · 轉往 <Link to={`/items/${disposition.target_item_id}`}>目標項目</Link></> : null}
                          {disposition.reason ? ` · ${disposition.reason}` : ''}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {dialog?.kind === 'return' ? (
        <ReturnBalanceModal
          balance={dialog.balance}
          onClose={() => setDialog(null)}
          onSubmit={(payload) => act(() => api.returnBalance(dialog.balance.id, payload), '結餘已記錄為已退回')}
        />
      ) : null}
      {dialog?.kind === 'transfer' ? (
        <TransferBalanceModal
          balance={dialog.balance}
          onClose={() => setDialog(null)}
          onSubmit={(payload) => act(() => api.transferBalance(dialog.balance.id, payload), '結餘已轉用至另一付款項目')}
        />
      ) : null}
    </div>
  );
}

function ReturnBalanceModal({ balance, onClose, onSubmit }) {
  const [amount, setAmount] = useState(centsToInput(balance.pending_cents));
  const [reason, setReason] = useState('');
  const [handledOn, setHandledOn] = useState(new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10));
  const cents = parseAmountToCents(amount);
  return (
    <Modal title="記錄結餘已退回" subtitle={`待處理 ${money(balance.pending_cents)}`} onClose={onClose}>
      <div className="stack">
        <label className="field"><span>退回金額（HKD）</span><input value={amount} onChange={(event) => setAmount(event.target.value)} data-testid="return-amount" /></label>
        <label className="field"><span>處理日期</span><input type="date" value={handledOn} onChange={(event) => setHandledOn(event.target.value)} data-testid="return-date" /></label>
        <label className="field"><span>原因（必填）</span><textarea value={reason} onChange={(event) => setReason(event.target.value)} data-testid="return-reason" /></label>
      </div>
      <div className="modal-actions">
        <button type="button" onClick={onClose}>取消</button>
        <button
          type="button"
          className="primary"
          disabled={cents === null || cents <= 0 || cents > balance.pending_cents || reason.trim().length < 2}
          data-testid="return-submit"
          onClick={() => onSubmit({ amount_cents: cents, reason: reason.trim(), handled_on: handledOn })}
        >
          儲存
        </button>
      </div>
    </Modal>
  );
}

function TransferBalanceModal({ balance, onClose, onSubmit }) {
  const session = useSession();
  const [amount, setAmount] = useState(centsToInput(balance.pending_cents));
  const [reason, setReason] = useState('');
  const [targetDueId, setTargetDueId] = useState('');
  const [handledOn, setHandledOn] = useState(new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10));
  const ledger = useAsync(() => api.ledger({ from_month: '2000-01' }), []);
  const cents = parseAmountToCents(amount);

  const candidates = (ledger.data ? ledger.data.items : [])
    .filter((item) => item.status === 'issued' && !item.historical && item.id !== balance.item_id)
    .flatMap((item) => item.dues
      .filter((due) => due.member_id === balance.member_id && due.remaining_cents > 0)
      .map((due) => ({ item, due })));

  return (
    <Modal title="轉用結餘至另一付款項目" subtitle={`${nameOf(session.members, balance.member_id)} · 待處理 ${money(balance.pending_cents)}`} onClose={onClose}>
      <div className="banner info small" style={{ marginBottom: 12 }}>
        轉用金額不能超過可用結餘，也不能超過目標應付款的仍欠金額；來源與目標兩邊都會留下可追溯關聯。
      </div>
      {ledger.loading ? <Loading /> : null}
      <div className="stack">
        <label className="field">
          <span>目標應付款</span>
          <select value={targetDueId} onChange={(event) => setTargetDueId(event.target.value)} data-testid="transfer-target">
            <option value="">請選擇</option>
            {candidates.map(({ item, due }) => (
              <option key={due.due_id} value={due.due_id}>
                {item.title} · 仍欠 {money(due.remaining_cents)}
              </option>
            ))}
          </select>
        </label>
        <label className="field"><span>轉用金額（HKD）</span><input value={amount} onChange={(event) => setAmount(event.target.value)} data-testid="transfer-amount" /></label>
        <label className="field"><span>處理日期</span><input type="date" value={handledOn} onChange={(event) => setHandledOn(event.target.value)} data-testid="transfer-date" /></label>
        <label className="field"><span>備註（選填）</span><input value={reason} onChange={(event) => setReason(event.target.value)} data-testid="transfer-reason" /></label>
      </div>
      <div className="modal-actions">
        <button type="button" onClick={onClose}>取消</button>
        <button
          type="button"
          className="primary"
          disabled={!targetDueId || cents === null || cents <= 0 || cents > balance.pending_cents}
          data-testid="transfer-submit"
          onClick={() => onSubmit({ amount_cents: cents, target_due_id: targetDueId, reason: reason.trim() || undefined, handled_on: handledOn })}
        >
          儲存
        </button>
      </div>
    </Modal>
  );
}
